/**
 * QL-315 — Asset Class Forecast Chart.
 *
 * A 100%-normalised stacked bar of the asset-class mix over time. Purely
 * informational: nothing gates on it, nothing is blocked by it, and it carries
 * no basis hash or placement guard.
 *
 * One snapshot serves the chart at two scopes: goal-scoped (the goal's
 * portfolios) and instance-scoped (every portfolio of every goal). Goals
 * partition portfolios — `goal_portfolio.goalId` is a non-nullable scalar FK
 * with no M:N table — so the instance-scoped merge counts nothing twice.
 *
 * Pure types module: no I/O, no dependencies.
 */

/** The three toggle positions. A window over the stored points, not a re-compute. */
export type AssetClassForecastHorizon = "1Y" | "5Y" | "10Y";

export const ASSET_CLASS_FORECAST_HORIZONS = ["1Y", "5Y", "10Y"] as const satisfies readonly [
  AssetClassForecastHorizon,
  ...AssetClassForecastHorizon[],
];

/**
 * QL-414 — the one range a PDF chart block prints (a PDF has no toggle).
 * Absent or unrecognised means `10Y`, what every block printed before the
 * setting existed.
 */
export const PDF_DEFAULT_TIME_RANGE: AssetClassForecastHorizon = "10Y";

export function resolvePdfTimeRange(value: unknown): AssetClassForecastHorizon {
  return (ASSET_CLASS_FORECAST_HORIZONS as readonly unknown[]).includes(value)
    ? (value as AssetClassForecastHorizon)
    : PDF_DEFAULT_TIME_RANGE;
}

/** Number of quarter-end points stored. `1Y` shows exactly these. */
export const ASSET_CLASS_FORECAST_QUARTER_POINTS = 4;

/** Number of year-end points stored. `5Y` shows the first five, `10Y` all ten. */
export const ASSET_CLASS_FORECAST_YEAR_POINTS = 10;

/**
 * Asset classes rendered as their own band before the tail collapses into
 * "Other". Asset classes are tenant-configured with no fixed count, so a
 * collection with a dozen of them would otherwise produce an unreadable
 * stacked bar. Six plus Other plus Unknown is already eight bands; past that
 * the colours stop being tellable apart in a stacked bar. Classes are ranked by
 * their total share across the rendered points, so the bands that survive are
 * the ones that actually carry weight.
 */
export const ASSET_CLASS_FORECAST_MAX_SEGMENTS = 6;

/** Sentinel ids for the two synthetic bands. Never real `asset_class.id` values. */
export const ASSET_CLASS_FORECAST_UNKNOWN_ID = "__unknown__";
export const ASSET_CLASS_FORECAST_OTHER_ID = "__other__";

/** One asset class's share of a single point, in percent of that point's total. */
export type AssetClassForecastShare = {
  /** `asset_class.id`, or one of the two sentinel ids. */
  assetClassId: string;
  /** Localized `asset_class.nameI18n` in the flow's language. */
  assetClassName: string;
  /** Percent of the point's projected value, 0..100. */
  percentage: number;
};

/** One bar, or the one stored point that is never drawn. */
export type AssetClassForecastPoint = {
  /**
   * `quarter` and `year` are the 14 drawn grid dates; `baseline` is the existing
   * portfolio before any change, present only on follow-ups.
   *
   * `anniversary` is the exception: twelve months after the anchor, stored but
   * never rendered. The year points are calendar year-ends, so the first of
   * them sits anywhere from 12 to 23 months out depending on which month the
   * flow was started — fine for a chart labelled "2027", useless for a
   * compliance gate that has to mean the same thing every time. QL-313 reads
   * this point and nothing else does.
   */
  kind: "anniversary" | "baseline" | "quarter" | "year";
  /** End-of-month ISO date ("YYYY-MM-DD"). Null on the baseline point. */
  date: string | null;
  /** Pre-rendered axis label — "Q2 2026", "2031", or the baseline caption. */
  label: string;
  /**
   * The merged mix. Sums to 100 (within rounding) unless the point is unfunded.
   *
   * This is the mix at THIS date, not the target allocation: each class has
   * been grown by its own expected return since the money went in, so a 50/50
   * target reads equity-heavy at the far end of the horizon. The model assumes
   * the portfolio is never rebalanced.
   */
  shares: AssetClassForecastShare[];
  /**
   * Combined unmeasured share of this point: product weight with no
   * `productAssetAllocations` rows, plus weight in an asset class whose
   * `equitySharePct` is NULL. Rendered as the Unknown band by QL-313's equity
   * chart; see `unclassifiedPct` for what this chart's own band uses.
   */
  unknownPct: number;
  /** Product weight with no asset-class allocation at all. This chart's Unknown band. */
  unclassifiedPct: number;
  /** Weight sitting in a named asset class whose `equitySharePct` is NULL. */
  unmeasuredClassPct: number;
  /** Total projected value of the portfolios at this date, in the flow's currency. */
  projectedValue: number;
  /**
   * The blended equity share QL-313 gates on, 0..100. Null on the
   * instance-scoped row, whose merge has no ceiling to be read against because
   * `maxEquitySharePct` is defined per goal.
   */
  equitySharePct: number | null;
  /**
   * QL-314 — the blended portfolio volatility at this point, annual %, computed
   * via `σp = √(ΣᵢΣⱼ wᵢwⱼσᵢσⱼρᵢⱼ)`. In Asset Classes mode the blend runs over
   * the merged asset-class vector and a class enters on its
   * `expectedVolatility` alone; in Products mode (QL-655) it runs over the
   * drifted product vector with the products' own volatility and the product
   * correlation matrix. Null on the instance-scoped row (same reason as
   * equitySharePct — ceilings are per goal), when nothing in the mix has a
   * volatility configured, and (QL-655) when a correlation pair among the
   * measured holdings is missing — a gap is not read as ρ = 0.
   */
  volatilityPct: number | null;
  /**
   * QL-314 — the share of this point that `volatilityPct` could NOT see. In
   * Asset Classes mode: product weight with no asset-class allocation, plus
   * weight in a class with no `expectedVolatility`. In Products mode (QL-655):
   * holdings outside the collection, plus products with no
   * `expectedVolatility`. Either way it enters the blend as σ = 0 and biases
   * it downward, so the volatility chart shows this as its coverage warning.
   * Independent of `unknownPct`, which is about equity share.
   */
  volatilityUnknownPct: number;
};

/**
 * The stored snapshot. Written goal-scoped (`asset_class_forecast`) and
 * instance-scoped (`instance_asset_class_forecast`); the instance row carries
 * only the vector and the unknown split, because the derived measures'
 * ceilings are defined per goal.
 */
export type AssetClassForecastSnapshot = {
  /**
   * Points in render order: baseline (if any), the anniversary gate point, then
   * the 4 quarters, then the 10 years. Read them through
   * {@link selectAssetClassForecastPoints} rather than filtering by hand — it is
   * what keeps the anniversary point out of the picture.
   */
  points: AssetClassForecastPoint[];
  /**
   * Every asset class appearing in any point, in `asset_class.order`, with the
   * two sentinel bands last. The chart's legend and colour assignment read
   * this so band colours stay stable across horizons and across bars.
   */
  legend: Array<{ assetClassId: string; assetClassName: string }>;
  currency: string;
  /** True when the tail was collapsed into "Other". */
  hasOtherBand: boolean;
  timestamp: string;
  /**
   * QL-493 — goal-scoped rows only: the portfolios whose own forecast fed this
   * merge. A decided portfolio missing here was dropped (its forecast is
   * absent or errored), which the compliance charts must treat as not
   * evaluable rather than measure the goal without it. Absent on rows written
   * before it was recorded and on the instance-scoped row.
   */
  contributedPortfolioIds?: string[];
};

/** An error snapshot, stored in place of the data when generation fails. */
export type AssetClassForecastErrorSnapshot = {
  error: string;
  timestamp: string;
};

/** The subset of a forecast point the horizon selector reads. */
export type AssetClassForecastSelectable = {
  kind: AssetClassForecastPoint["kind"];
  /** Absent on derived point shapes (the volatility chart's) that are pre-filtered. */
  projectedValue?: number;
};

/**
 * The points to draw for a horizon: 1Y is the four quarters; 5Y is the anchor
 * year's end plus the next four year-ends, 10Y plus the next nine — five and
 * ten points, the same for every chart over the grid (asset class forecast,
 * volatility, equity share; decided 2026-09-25). Each is preceded by the
 * baseline when there is one. The former opt-in "leading quarter" for the
 * volatility line is gone: the anchor-year point is what starts every series.
 *
 * Two things never come back. The `anniversary` point is a gate input, not a
 * bar. And a point worth nothing is dropped: before its first deposit lands a
 * portfolio contributes no weight, so an early bar can have no mix to show, and
 * an empty bar reads as "all of it is in some invisible class" rather than "not
 * funded yet". Dropping it here rather than in a renderer is what keeps the
 * screen and the PDF showing the same set of bars.
 *
 * Generic over the point shape so the volatility chart, whose points carry no
 * `projectedValue` (they are already filtered to funded points with a figure),
 * shares this one rule instead of keeping a copy.
 */
export function selectAssetClassForecastPoints<P extends AssetClassForecastSelectable>(
  points: P[],
  horizon: AssetClassForecastHorizon,
): P[] {
  const drawable = points.filter((p) => p.projectedValue === undefined || p.projectedValue > 0);
  const baseline = drawable.filter((p) => p.kind === "baseline");
  const quarters = drawable.filter((p) => p.kind === "quarter");
  if (horizon === "1Y") {
    return [...baseline, ...quarters];
  }
  const years = drawable.filter((p) => p.kind === "year");
  return [...baseline, ...years.slice(0, horizon === "5Y" ? 5 : ASSET_CLASS_FORECAST_YEAR_POINTS)];
}
